import React, { useCallback, useState } from 'react'

import { Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, oneOf, shape, string,
} from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { formatLongDate } from 'Utils/FormatDate'

import DoneStep from './Components/DoneStep'
import ScheduleStep from './Components/ScheduleStep'
import TierStep from './Components/TierStep'
import { formatChangeDate } from './planChangeOptions'
import {
  PLAN_CHANGE_KIND, PLAN_TYPE, TIER,
} from '../../constants'
import { availablePlansShape, catalogValuesShape, planChangesShape } from '../../propTypes'

const KEY = 'subscription_management.plan_change'

const STEP = { TIER: 'tier', SCHEDULE: 'schedule', DONE: 'done' }

function PlanChangeModal({
  subscription,
  availablePlans,
  catalogValues = null,
  upgradeUrl,
  onClose,
  onSchedule,
  isScheduling = false,
  locale = 'en',
}) {
  const [step, setStep] = useState(STEP.TIER)
  const [selection, setSelection] = useState(null)
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const { tier, billingTerm, planChanges } = subscription
  const { effectiveAt } = planChanges
  const effectiveDate = formatLongDate(effectiveAt, locale, 'UTC')
  const tierName = (planTier) => translate(`${KEY}.tiers.${planTier}`)
  const isDowngrade = selection?.kind === PLAN_CHANGE_KIND.DOWNGRADE

  const handleSelect = useCallback((target) => {
    setSelection(target)
    setStep(STEP.SCHEDULE)
  }, [])

  const handleBack = useCallback(() => {
    setSelection(null)
    setStep(STEP.TIER)
  }, [])

  const handleConfirm = useCallback(async () => {
    const scheduled = await onSchedule(selection.target)
    if (scheduled) setStep(STEP.DONE)
  }, [selection, onSchedule])

  const scheduleHeader = () => {
    if (!isDowngrade) return translate(`${KEY}.actions.switch_to_monthly`)
    const titleKey = selection.billingTerm === PLAN_TYPE.ANNUAL
      ? `${KEY}.actions.downgrade_annual`
      : `${KEY}.schedule.title_downgrade_monthly`
    return translate(titleKey, { plan: tierName(selection.tier) })
  }

  const confirmLabel = translate(
    `${KEY}.schedule.${isDowngrade ? 'confirm_downgrade' : 'confirm_to_monthly'}`,
    { date: effectiveAt && formatChangeDate(effectiveAt, locale) },
  )

  const steps = {
    [STEP.TIER]: {
      header: translate(`${KEY}.title`),
      content: (
        <TierStep
          tier={tier}
          billingTerm={billingTerm}
          targets={planChanges.targets}
          availablePlans={availablePlans}
          catalogValues={catalogValues}
          upgradeUrl={upgradeUrl}
          effectiveDate={effectiveAt && formatChangeDate(effectiveAt, locale)}
          onSelect={handleSelect}
          translate={translate}
        />
      ),
    },
    [STEP.SCHEDULE]: selection && {
      header: scheduleHeader(),
      content: (
        <ScheduleStep
          selection={selection}
          tier={tier}
          billingTerm={billingTerm}
          availablePlans={availablePlans}
          effectiveDate={effectiveDate}
          translate={translate}
        />
      ),
      primaryAction: {
        content: confirmLabel,
        buttonProps: { type: 'primary', onClick: handleConfirm, disabled: isScheduling },
      },
      secondaryAction: {
        content: isDowngrade
          ? translate(`${KEY}.keep_plan`, { plan: tierName(tier) })
          : translate(`${KEY}.schedule.back`),
        buttonProps: { onClick: handleBack, disabled: isScheduling },
      },
    },
    [STEP.DONE]: selection && {
      header: translate(`${KEY}.done.title`),
      content: <DoneStep selection={selection} tier={tier} effectiveDate={effectiveDate} translate={translate} />,
      primaryAction: {
        content: translate(`${KEY}.done.button`),
        buttonProps: { type: 'primary', onClick: onClose },
      },
    },
  }
  const current = steps[step]

  return (
    <Modal
      isOpen
      onClose={onClose}
      header={current.header}
      width="medium"
      canClose
      disableClose={isScheduling}
      customClass="plan-change-modal"
      primaryAction={current.primaryAction}
      secondaryAction={current.secondaryAction}
    >
      {current.content}
    </Modal>
  )
}

PlanChangeModal.propTypes = {
  subscription: shape({
    tier: oneOf([TIER.ESSENTIALS, TIER.PREMIUM]).isRequired,
    billingTerm: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.ANNUAL]).isRequired,
    planChanges: planChangesShape.isRequired,
  }).isRequired,
  availablePlans: availablePlansShape.isRequired,
  catalogValues: catalogValuesShape,
  upgradeUrl: string.isRequired,
  onClose: func.isRequired,
  onSchedule: func.isRequired,
  isScheduling: bool,
  locale: string,
}

export default PlanChangeModal
